import React, { useState, useEffect } from "react";
import "../../styles/common/analysisComponent.scss";
import { parseJsonRobustly } from "../../helper/jsonUtils";
import { AddIcon, EditIcon, DeleteIcon } from "../../icons";
import DeleteConfirmPopUp from "./deleteConfirmPopUp";
import {
  updatePageForCourse,
  updatePageForSubject,
  updatePageForTopic,
  updatePageForSubtopic,
  getPageForCourse,
  getPageForSubject,
  getPageForTopic,
  getPageForSubtopic,
} from "../../helper/apicalls/course";
import GlobalLoader from "./GlobalLoader";

const AnalysisComponent = ({
  data,
  title = "Analysis",
  onUpdate,
  pageId,
  pageData,
  context,
  courseKey,
  subjectKey,
  topicKey,
  subTopicKey,
  contentIndex,
  onAddNext,
  permission,
  allowEditWithoutPersist = false,
  provideUpdatedBuilder,
}) => {
  const [question, setQuestion] = useState("");
  const [explanation, setExplanation] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [category, setCategory] = useState("");
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const buildUpdatedItemFromOriginal = (originalItem) => {
    let out;
    if (typeof originalItem === "string") {
      try {
        out = JSON.parse(originalItem);
      } catch (e) {
        out = {};
      }
    } else {
      const cloned = JSON.parse(JSON.stringify(originalItem || {}));
      out = cloned && typeof cloned === "object" ? cloned : {};
    }

    const contentObj = {
      questions: [
        {
          question,
          explanation,
        },
      ],
    };

    out.type = "analysis";
    out.content = JSON.stringify(contentObj);

    return out;
  };

  useEffect(() => {
    if (typeof provideUpdatedBuilder === "function") {
      provideUpdatedBuilder((originalItem) =>
        buildUpdatedItemFromOriginal(originalItem),
      );
    }
  }, [provideUpdatedBuilder, question, explanation]);

  const doUpdate = async () => {
    if (!pageId || !context) {
      if (allowEditWithoutPersist && typeof onUpdate === "function") {
        const tempOriginal = { type: "analysis", content: "{}" };
        const updatedItem = buildUpdatedItemFromOriginal(tempOriginal);
        onUpdate({ content: [updatedItem] });
        setEditMode(false);
      }
      return;
    }
    let contentArray = [];
    try {
      let rawContent = pageData?.content;
      if (typeof rawContent === "string") {
        const parsed = JSON.parse(rawContent);
        if (Array.isArray(parsed)) contentArray = parsed;
        else if (parsed && typeof parsed === "object") contentArray = [parsed];
      } else if (Array.isArray(rawContent)) {
        contentArray = rawContent;
      } else if (rawContent && typeof rawContent === "object") {
        contentArray = [rawContent];
      }
    } catch (e) {
      return;
    }

    if (!Array.isArray(contentArray)) return;
    if (
      typeof contentIndex !== "number" ||
      contentIndex < 0 ||
      contentIndex >= contentArray.length
    )
      return;

    const updatedArray = [...contentArray];
    const originalItem = updatedArray[contentIndex];
    const updatedItem = buildUpdatedItemFromOriginal(originalItem);
    updatedArray[contentIndex] = updatedItem;

    const payload = { id: pageId, content: updatedArray };
    let updated;

    setLoading(true);
    try {
      switch (context) {
        case "course":
          await updatePageForCourse(payload);
          updated = await getPageForCourse(courseKey, pageId);
          break;
        case "subject":
          await updatePageForSubject(payload);
          updated = await getPageForSubject(subjectKey, pageId);
          break;
        case "topic":
          await updatePageForTopic(payload);
          updated = await getPageForTopic(topicKey, pageId);
          break;
        case "subtopic":
          await updatePageForSubtopic(payload);
          updated = await getPageForSubtopic(subTopicKey, pageId);
          break;
        default:
          updated = payload;
      }

      if (onUpdate) onUpdate(updated);
      setEditMode(false);
    } finally {
      setLoading(false);
    }
  };

  const doDelete = async () => {
    if (!pageId || !context) {
      if (allowEditWithoutPersist && typeof onUpdate === "function") {
        onUpdate({ content: [] });
        setIsDeleteConfirmOpen(false);
      }
      return;
    }
    let contentArray = [];
    try {
      let rawContent = pageData?.content;
      if (typeof rawContent === "string") {
        const parsed = JSON.parse(rawContent);
        if (Array.isArray(parsed)) contentArray = parsed;
        else if (parsed && typeof parsed === "object") contentArray = [parsed];
      } else if (Array.isArray(rawContent)) {
        contentArray = rawContent;
      } else if (rawContent && typeof rawContent === "object") {
        contentArray = [rawContent];
      }
    } catch (e) {
      return;
    }

    if (!Array.isArray(contentArray)) return;
    if (
      typeof contentIndex !== "number" ||
      contentIndex < 0 ||
      contentIndex >= contentArray.length
    )
      return;

    const updatedArray = contentArray.filter((_, idx) => idx !== contentIndex);

    const payload = { id: pageId, content: updatedArray };
    let updated;

    setLoading(true);
    try {
      switch (context) {
        case "course":
          await updatePageForCourse(payload);
          updated = await getPageForCourse(courseKey, pageId);
          break;
        case "subject":
          await updatePageForSubject(payload);
          updated = await getPageForSubject(subjectKey, pageId);
          break;
        case "topic":
          await updatePageForTopic(payload);
          updated = await getPageForTopic(topicKey, pageId);
          break;
        case "subtopic":
          await updatePageForSubtopic(payload);
          updated = await getPageForSubtopic(subTopicKey, pageId);
          break;
        default:
          updated = payload;
      }

      if (onUpdate) onUpdate(updated);
      setIsDeleteConfirmOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const Button = ({
    children,
    onClick,
    variant,
    size,
    className,
    disabled,
  }) => {
    const baseClass = "analysis-button";
    const variantClass = variant ? `analysis-button--${variant}` : "";
    const sizeClass = size ? `analysis-button--${size}` : "";
    const disabledClass = disabled ? "analysis-button--disabled" : "";
    const classes = `${baseClass} ${variantClass} ${sizeClass} ${disabledClass} ${
      className || ""
    }`;

    return (
      <button className={classes} onClick={onClick} disabled={disabled}>
        {children}
      </button>
    );
  };

  useEffect(() => {
    if (!data) {
      setQuestion("");
      setExplanation("");
      setCategory("");
      return;
    }

    try {
      let processed = data;

      if (typeof data === "string") {
        const parsed = parseJsonRobustly(data);
        processed =
          parsed !== undefined && parsed !== null && typeof parsed === "object"
            ? parsed
            : data;
      }

      if (processed && typeof processed === "object" && processed.content) {
        processed = processed.content;
      }

      if (typeof processed === "string") {
        setQuestion(processed);
        setExplanation("");
        return;
      }

      if (processed && typeof processed === "object") {
        let source = processed;

        if (
          Array.isArray(processed.questions) &&
          processed.questions.length > 0
        ) {
          source = processed.questions[0];
        }

        const q =
          source.question || source.title || source.text || source.prompt || "";
        const exp = source.explanation || source.reason || "";
        const cat =
          source.categories ||
          source.category ||
          processed.categories ||
          processed.category ||
          "";

        setQuestion(typeof q === "string" ? q : JSON.stringify(q));
        setExplanation(
          typeof exp === "string" ? exp : exp ? JSON.stringify(exp) : "",
        );
        setCategory(
          typeof cat === "string" ? cat : cat ? JSON.stringify(cat) : "",
        );
        return;
      }

      setQuestion(String(processed));
      setExplanation("");
      setCategory("");
    } catch (error) {
      console.error("Error processing analysis data:", error);
      setQuestion(typeof data === "string" ? data : JSON.stringify(data));
      setExplanation("");
      setCategory("");
    }
  }, [data]);

  return (
    <>
      {loading && <GlobalLoader visible={loading} text="Processing..." />}
      {isDeleteConfirmOpen && (
        <DeleteConfirmPopUp
          isConfirmDeleteOpen={isDeleteConfirmOpen}
          setIsConfirmDeleteOpen={setIsDeleteConfirmOpen}
          handleDelete={doDelete}
          title="Delete analysis"
          message="Are you sure you want to delete this analysis item? This action cannot be undone."
          confirmLabel="Delete"
          cancelLabel="Cancel"
        />
      )}
      {pageData?.content?.length === 0 ? (
        <div>No data</div>
      ) : (
        <div className="analysis-component">
          <div className="analysis-header">
            <h3 className="analysis-title">{title}</h3>
            {category && (
              <span className="question-category-chip">
                {String(category)
                  .replace(/_/g, " ")
                  .replace(/\b\w/g, (l) => l.toUpperCase())}
              </span>
            )}
          </div>

          <div className="analysis-question">
            {editMode ? (
              <>
                <div
                  className={`analysis-textfield analysis-textfield--fullwidth analysis-question__input`}
                >
                  <label className="analysis-textfield__label">Question</label>
                  <input
                    className="analysis-textfield__input"
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                  />
                </div>
                <div
                  className={`analysis-textfield analysis-textfield--fullwidth analysis-question__explanation-input`}
                >
                  <label className="analysis-textfield__label">
                    Explanation (optional)
                  </label>
                  <textarea
                    className="analysis-textfield__input"
                    rows={3}
                    value={explanation}
                    onChange={(e) => setExplanation(e.target.value)}
                  />
                </div>
              </>
            ) : (
              <>
                <h3 className="analysis-question__title">{question}</h3>
                {explanation && (
                  <div className="analysis-explanation">
                    <strong>Explanation:</strong> {explanation}
                  </div>
                )}
              </>
            )}
          </div>

          {permission && (pageId || allowEditWithoutPersist) && (
            <div className="analysis-actions">
              {editMode ? (
                <>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => setEditMode(false)}
                    className="analysis-action-button"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={doUpdate}
                    className="analysis-action-button"
                  >
                    Save
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => setEditMode(true)}
                    className="analysis-action-button"
                  >
                    <EditIcon className="toolbar-button-icon" />
                    Edit
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => setIsDeleteConfirmOpen(true)}
                    className="analysis-action-button"
                  >
                    <DeleteIcon className="toolbar-button-icon" />
                    Delete
                  </Button>
                  {onAddNext && (
                    <Button
                      variant="contained"
                      size="small"
                      onClick={onAddNext}
                      className="analysis-action-button"
                    >
                      <AddIcon className="toolbar-button-icon" />
                      Add Next
                    </Button>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default AnalysisComponent;
